import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

// ── Helper ─────────────────────────────────────────────────────────────────────

const apiCall = async (url) => {
  const res = await fetch(url, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `Request failed (${res.status})`);
  return data;
};

// ── Stat Card ──────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, color }) {
  return (
    <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-400">{label}</p>
          <p className="text-2xl font-bold text-gray-100 mt-1">{value}</p>
        </div>
        <span className="text-3xl">{icon}</span>
      </div>
    </div>
  );
}

// ── Bar Chart ──────────────────────────────────────────────────────────────────

function PostsPerDayChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Posts Per Day (Last 7 Days)</h3>
        <p className="text-sm text-gray-500">No data available</p>
      </div>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
      <h3 className="text-sm font-semibold text-gray-300 mb-4">Posts Per Day (Last 7 Days)</h3>
      <div className="flex items-end justify-between gap-3 h-48">
        {data.map((d, i) => {
          const heightPct = (d.count / maxValue) * 100;
          return (
            <div key={i} className="flex-1 flex flex-col items-center">
              <span className="text-xs text-gray-400 mb-1">{d.count}</span>
              <div className="w-full flex-1 flex items-end">
                <div
                  className="w-full bg-blue-600 rounded-t transition-all hover:bg-blue-500"
                  style={{ height: `${Math.max(heightPct, 4)}%` }}
                  title={`${d.label}: ${d.count} posts`}
                />
              </div>
              <span className="text-xs text-gray-500 mt-2">{d.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Top Posts Table ────────────────────────────────────────────────────────────

function TopPostsTable({ posts }) {
  if (!posts || posts.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Top Posts</h3>
        <p className="text-sm text-gray-500">No posts yet</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
      <h3 className="text-sm font-semibold text-gray-300 mb-4">Top Posts</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700 text-left text-gray-400">
              <th className="py-2 px-3 font-medium">Post</th>
              <th className="py-2 px-3 font-medium">Platform</th>
              <th className="py-2 px-3 font-medium text-center">Likes</th>
              <th className="py-2 px-3 font-medium text-center">Comments</th>
              <th className="py-2 px-3 font-medium text-center">Shares</th>
              <th className="py-2 px-3 font-medium text-center">Impressions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((post, i) => (
              <tr key={post.id || i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                <td className="py-3 px-3 max-w-xs truncate text-gray-200" title={post.content}>
                  {post.content?.slice(0, 80) || '—'}
                  {post.content?.length > 80 ? '…' : ''}
                </td>
                <td className="py-3 px-3">
                  <span className="inline-flex items-center gap-1 text-gray-300 capitalize">
                    {post.platform === 'facebook' && '📘'}
                    {post.platform === 'instagram' && '📸'}
                    {post.platform === 'x' && '🐦'}
                    {post.platform === 'linkedin' && '💼'}
                    {post.platform || '—'}
                  </span>
                </td>
                <td className="py-3 px-3 text-center text-gray-300">{post.likes ?? 0}</td>
                <td className="py-3 px-3 text-center text-gray-300">{post.comments ?? 0}</td>
                <td className="py-3 px-3 text-center text-gray-300">{post.shares ?? 0}</td>
                <td className="py-3 px-3 text-center text-gray-300">{post.impressions ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Posting Calendar ───────────────────────────────────────────────────────────

function PostingCalendar({ scheduledPosts }) {
  if (!scheduledPosts || scheduledPosts.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Upcoming Scheduled Posts</h3>
        <p className="text-sm text-gray-500">No upcoming posts scheduled</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
      <h3 className="text-sm font-semibold text-gray-300 mb-4">Upcoming Scheduled Posts</h3>
      <div className="space-y-3">
        {scheduledPosts.map((post, i) => {
          const date = post.scheduledFor || post.scheduledAt || post.date;
          const dateObj = date ? new Date(date) : null;
          return (
            <div
              key={post.id || i}
              className="flex items-center justify-between bg-gray-700/40 rounded-lg px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col items-center justify-center w-12 h-12 bg-gray-700 rounded-lg flex-shrink-0">
                  {dateObj ? (
                    <>
                      <span className="text-xs text-gray-400 uppercase">
                        {dateObj.toLocaleDateString('en-US', { weekday: 'short' })}
                      </span>
                      <span className="text-lg font-bold text-gray-100 leading-none">
                        {dateObj.getDate()}
                      </span>
                    </>
                  ) : (
                    <span className="text-xs text-gray-500">TBD</span>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-200 truncate max-w-xs" title={post.content}>
                    {post.content?.slice(0, 60) || 'Untitled post'}
                    {post.content?.length > 60 ? '…' : ''}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-gray-400 capitalize">
                      {post.platform === 'facebook' && '📘'}
                      {post.platform === 'instagram' && '📸'}
                      {post.platform === 'x' && '🐦'}
                      {post.platform === 'linkedin' && '💼'}
                      {post.platform || '—'}
                    </span>
                    {dateObj && (
                      <span className="text-xs text-gray-500">
                        {dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <span
                className={`text-xs rounded-full px-2 py-1 ${
                  post.status === 'approved'
                    ? 'bg-green-600 text-white'
                    : post.status === 'pending' || post.status === 'draft'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-gray-600 text-gray-300'
                }`}
              >
                {post.status || 'pending'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function SocialAnalytics() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPosts: 0,
    postedThisWeek: 0,
    pendingReview: 0,
    engagementRate: 0,
  });
  const [postsPerDay, setPostsPerDay] = useState([]);
  const [topPosts, setTopPosts] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);

  useEffect(() => {
    loadAnalytics();
    const interval = setInterval(loadAnalytics, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadAnalytics = async () => {
    try {
      const data = await apiCall('/api/social/analytics');

      if (data.stats) {
        setStats({
          totalPosts: data.stats.totalPosts ?? 0,
          postedThisWeek: data.stats.postedThisWeek ?? 0,
          pendingReview: data.stats.pendingReview ?? 0,
          engagementRate: data.stats.engagementRate ?? 0,
        });
      }
      if (data.postsPerDay) setPostsPerDay(data.postsPerDay);
      if (data.topPosts) setTopPosts(data.topPosts);
      if (data.scheduledPosts) setScheduledPosts(data.scheduledPosts);
    } catch (e) {
      console.error('Failed to load analytics:', e);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <div className="text-lg text-gray-400">Loading analytics…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Social Analytics</h1>
            <p className="text-sm text-gray-400 mt-1">
              Performance overview of your social media content.
            </p>
          </div>
          <button
            onClick={loadAnalytics}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium"
          >
            ↻ Refresh
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Total Posts"
            value={stats.totalPosts}
            icon="📝"
            color="blue"
          />
          <StatCard
            label="Posted This Week"
            value={stats.postedThisWeek}
            icon="🚀"
            color="green"
          />
          <StatCard
            label="Pending Review"
            value={stats.pendingReview}
            icon="⏳"
            color="yellow"
          />
          <StatCard
            label="Engagement Rate"
            value={`${stats.engagementRate}%`}
            icon="📊"
            color="purple"
          />
        </div>

        {/* Bar chart */}
        <div className="mb-6">
          <PostsPerDayChart data={postsPerDay} />
        </div>

        {/* Top posts + calendar */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TopPostsTable posts={topPosts} />
          <PostingCalendar scheduledPosts={scheduledPosts} />
        </div>
      </div>
    </div>
  );
}
